if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('form');
        this.form && this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

        // Give immediate feedback instead of waiting for Shopify to finish the
        // cart request and render the cart-drawer section. The drawer contents
        // are still refreshed in the background and errors remain handled below.
        if (this.cart && typeof this.cart.renderContentsWithoutOpening === 'function') {
          this.showAddedToCartMessage();
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;

            // We always show an inline "item added" confirmation (at the top of the
            // quick view / product buy box) instead of popping the cart drawer open
            // — on every screen size, not just mobile (matches a competitor reference
            // the merchant liked, and avoids a duplicate/redundant drawer popup on
            // desktop on top of the inline message). If the cart element doesn't
            // support the non-opening render (e.g. cart-notification instead of
            // cart-drawer), fall back to the normal renderContents().
            if (this.cart && typeof this.cart.renderContentsWithoutOpening === 'function') {
              this.cart.renderContentsWithoutOpening(response);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading-overlay__spinner').classList.add('hidden');
          });
      }

      showAddedToCartMessage() {
        // Pinned to the TOP of the quick view sheet / product buy box (not just
        // above the Add to cart button) so it's visible immediately without the
        // shopper needing to scroll — this is deliberately a different container
        // than `this` (the <product-form> element), which only wraps the button
        // row itself. .popup-modal__content__content is quick view's scrollable
        // sheet; .product__column__content / .main-product__container cover the
        // standalone product page. Falls back to `this` if none of those exist.
        const topContainer =
          this.closest('.popup-modal__content__content') ||
          this.closest('.product__column__content') ||
          this.closest('.main-product__container') ||
          this;
        if (!topContainer) return;

        let msg = topContainer.querySelector('.iov-added-to-cart-message');
        if (!msg) {
          msg = document.createElement('div');
          msg.className = 'iov-added-to-cart-message';
          msg.setAttribute('role', 'status');
          msg.hidden = true;
          msg.innerHTML =
            '<span class="iov-added-to-cart-message__icon" aria-hidden="true">' +
              '<svg viewBox="0 0 24 24" width="20" height="20" fill="none"><path d="M5 13l4 4L19 7" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
            '</span>' +
            '<span class="iov-added-to-cart-message__text">' +
              '<strong class="iov-added-to-cart-message__heading"></strong>' +
              '<a class="iov-added-to-cart-message__link"></a>' +
            '</span>';
          topContainer.insertAdjacentElement('afterbegin', msg);
        }

        const heading = msg.querySelector('.iov-added-to-cart-message__heading');
        const link = msg.querySelector('.iov-added-to-cart-message__link');
        if (heading) heading.textContent = (window.cartStrings && window.cartStrings.itemAdded) || 'Item added to your cart.';
        if (link) {
          link.textContent = (window.cartStrings && window.cartStrings.viewCart) || 'View cart';
          link.href = (window.routes && window.routes.cart_url) || '/cart';
        }

        msg.hidden = false;

        // Being first in the DOM isn't enough on its own — if the shopper had
        // already scrolled down (past the images in quick view, or further
        // down the product page) before adding to cart, the message would be
        // sitting off-screen above their current scroll position. scrollIntoView
        // finds whichever ancestor actually scrolls (the quick view panel, or
        // the page itself) and brings the message into view automatically.
        // behavior:'instant' is deliberate, not an oversight — this theme sets
        // `scroll-behavior: smooth` globally on <html> (see head-styles.liquid),
        // which combined with a scroll triggered right after inserting the
        // element into the DOM was unreliable (the animation could get dropped
        // silently instead of completing). An immediate jump guarantees the
        // confirmation is actually visible, which matters more here than an
        // animated transition.
        if (typeof msg.scrollIntoView === 'function') {
          msg.scrollIntoView({ behavior: 'instant', block: 'start' });
        }

        // Persistent by default — only auto-hides if the merchant turns that on
        // in Theme Editor → Cart drawer section settings (window.cartAtcSettings,
        // set in sections/cart-drawer.liquid).
        clearTimeout(this._iovAtcMessageTimeout);
        const atcSettings = window.cartAtcSettings || {};
        if (atcSettings.autoHide) {
          const delay = atcSettings.autoHideDelay || 4000;
          this._iovAtcMessageTimeout = setTimeout(() => {
            msg.hidden = true;
          }, delay);
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}

class stickyCartModal extends HTMLElement {
  constructor() {
    super();
    window.addEventListener('scroll', this.update.bind(this));
    this.update();
    this.querySelector('.sticky-cart-button').addEventListener('click', this.onClickHandler.bind(this));
    this.querySelector('.sticky-cart-button').addEventListener('keydown', this.onClickKeydownHandler.bind(this));
  }
  update(){
    const sectionId = this.dataset.section;
    const buttonContainer = document.getElementById(`product-form-${sectionId}`);
    const sect = this.closest('.shopify-section, .popup-modal__content__data');
    const hasSelectOption = sect?.querySelector('select-option-js');
    const customAddButton = buttonContainer?.querySelector('.select-options-button');
    const addButton = (hasSelectOption && customAddButton) ? customAddButton : buttonContainer?.querySelector('[name="add"]');
    const rangeToShowModal = this.getTop(addButton) + addButton.clientHeight;

    if(rangeToShowModal <= 0) return false;
    if(window.scrollY >= rangeToShowModal && !this.classList.contains('show-modal')){
      this.classList.add('show-modal');
      document.querySelector('body').classList.add('modal__sticky-cart');
      document.querySelector('.footer__content').classList.add('sticky-cart-modal_bottom_padding');
    }
    if(window.scrollY < rangeToShowModal && this.classList.contains('show-modal')){
      this.classList.remove('show-modal');
      document.querySelector('body').classList.remove('modal__sticky-cart');
    }
  }
  onClickKeydownHandler(event){
    if(event.code.toUpperCase() !== 'ENTER') return false;
    this.onClickHandler();
  }
  onClickHandler(){
    var item = document.querySelector(`.product__column__content [id*="variant-selects-"]`),
        btn_item = document.getElementById("product-container"),
        _y = (item ? this.getTop(item) : this.getTop(btn_item)) - 20;
    this.scrollTo(_y,700);
  }
  getTop(el) {
    return el.offsetTop + (el.offsetParent && this.getTop(el.offsetParent));
  }
  scrollTo(to, duration) {
    const element = document.scrollingElement || document.documentElement,
          start = element.scrollTop,
          change = to - start,
    animateScroll = function() {
      element.scrollTop = to;
    };
    animateScroll();
  }
}
customElements.define('sticky-cart-modal', stickyCartModal);
